import { tap, debounceTime } from 'rxjs/operators';
import { defineAsyncComponent, ref, computed, defineComponent, onUpdated, onMounted, watch } from 'vue';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { useCentralStore } from 'src/stores/central';
import { useSizeStore } from 'src/stores/size';
import { useViewStore } from 'src/stores/view';
import { useRouter, useRoute } from 'vue-router';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { openWindowPlugin } from "src/plugins/general/openWindow";
import { SetMetaPlugin } from 'src/plugins/meta/setMeta';
import { dtWithLocaleFormat } from 'src/plugins/date/dtWithLocale';
import { MetaBuilderLibrary } from 'src/plugins/meta/metaBuilder';
import { pageFooterIntPlugin } from 'src/plugins/i18n/local/pageFooter';
import landingSection from 'src/features/landing/components/landingSection.vue';
export default defineComponent({
    components: {
        landingSection,
        lazyLoad: defineAsyncComponent(() => import(
        /* webpackChunkName: "lazyLoad" */
        'components/image/lazyLoad.vue')),
        intersection: defineAsyncComponent(() => import(
        /* webpackChunkName: "Intersection" */
        'components/general/intersection.vue')),
        subscription: defineAsyncComponent(() => import(
        /* webpackChunkName: "Subscription" */
        'components/app/popup/subscription.vue'))
    },
    props: {
        forceShowFooter: Boolean
    },
    emits: ['in-view'],
    setup(props) {
        const { formatDateWithSysLocale } = dtWithLocaleFormat();
        const todayYear = formatDateWithSysLocale(new Date(), 'YYYY');
        const vueRouter = useRouter();
        const vueRoute = useRoute();
        const centralStore = useCentralStore();
        const sizeStore = useSizeStore();
        const viewStore = useViewStore();
        const routeCountry = computed(() => vueRoute.params.country?.toString() || "");
        const { t } = pageFooterIntPlugin();
        const { isHost, styleType } = AppGeneralVariables();
        const getSpacesList = [{
                title: 'Rent Commercial (Flexi)',
                display_name: t('Rent Commercial (Flexi)'),
                routeName: 'Search',
                query: {}
            }, {
                title: 'Search Commercial Spaces',
                display_name: 'Search Commercial Spaces',
                routeName: 'Home',
                query: {}
            }, {
                title: 'Klatch',
                display_name: 'Klatch',
                routeName: 'Klatch',
                query: {}
            }, ...(vueRoute.params.country === "sg"
                ? [{
                        title: 'Buy Credits and Save',
                        display_name: t('Buy Credits and Save'),
                        routeName: 'Credits Package',
                        query: {}
                    }]
                : []), {
                title: 'Host',
                display_name: t('Host'),
                routeName: 'Rent Out Spaces Flexibly',
                query: {}
            }, {
                title: 'Host Resource',
                display_name: t('Host Resource'),
                external: true,
                url: 'https://blog.getspaces.com/host-resources'
            }];
        const getPropertyList = [{
                title: 'Property Owners',
                display_name: t('Property Owners'),
                routeName: 'List your property',
                external: false,
                url: '',
                query: {}
            },
            {
                title: 'Commercial Leads',
                display_name: 'Commercial Leads',
                routeName: 'Commercial Leads',
                external: false,
                url: '',
                query: {}
            }];
        const tacList = [{
                title: 'Privacy Policy',
                display_name: t('Privacy Policy'),
                routeName: 'Policies Page',
                query: {
                    tabs: 'Privacy Policy'
                }
            }, {
                title: 'Terms of Use',
                display_name: t('Terms of Use'),
                routeName: 'Policies Page',
                query: {
                    tabs: 'Platform Terms of Use'
                }
            }];
        const { StoreMeta, StoreLink, StoreScript, setMetaScript } = SetMetaPlugin();
        function setStructuredData() {
            const metaLib = new MetaBuilderLibrary({
                currentMeta: StoreMeta.value,
                currentLink: StoreLink.value,
                currentScript: StoreScript.value
            });
            const breadCrumbData = [{
                    'position': 1,
                    'name': 'About',
                    'url': `https://${process.env.domain}/${routeCountry.value}/${vueRouter.resolve({
                        name: 'About'
                    }).href}`
                }, {
                    'position': 1,
                    'name': 'Contact Us',
                    'url': `https://${process.env.domain}/${routeCountry.value}/${vueRouter.resolve({
                        name: 'Contact Us'
                    }).href}`
                }, {
                    'position': 1,
                    'name': 'Sitemap',
                    'url': `https://${process.env.domain}${routeCountry.value}//${vueRouter.resolve({
                        name: 'Sitemap'
                    }).href}`
                }];
            for (let i = 0; i < getSpacesList.length; i++) {
                breadCrumbData.push({
                    'position': 1,
                    'name': getSpacesList[i].title,
                    'url': `https://${process.env.domain}/${routeCountry.value}/${vueRouter.resolve({
                        name: getSpacesList[i].routeName
                    }).href}`
                });
            }
            for (let i = 0; i < getPropertyList.length; i++) {
                breadCrumbData.push({
                    'position': 1,
                    'name': getPropertyList[i].title,
                    'url': `https://${process.env.domain}/${routeCountry.value}/${vueRouter.resolve({
                        name: getPropertyList[i].routeName
                    }).href}`
                });
            }
            for (let i = 0; i < tacList.length; i++) {
                breadCrumbData.push({
                    'position': 1,
                    'name': tacList[i].title,
                    'url': `https://${process.env.domain}/${routeCountry.value}/${vueRouter.resolve({
                        name: tacList[i].routeName
                    }).href}`
                });
            }
            metaLib.setSoftwareStructuredData(centralStore.platformInfo.ios || false);
            metaLib.setBreadcrumbStructuredData(breadCrumbData);
            setMetaScript(metaLib.getScript());
        }
        /***
         * Pass footer structure if footer is visible
         */
        const structureParsed = ref(false);
        const centralShowFooter = computed(() => viewStore.showFooter);
        if (props.forceShowFooter) {
            setStructuredData();
            structureParsed.value = true;
        }
        watch(centralShowFooter, (newValue, oldValue) => {
            if (!newValue || oldValue)
                return;
            if (structureParsed.value)
                return;
            setStructuredData();
            structureParsed.value = true;
        }, { immediate: true });
        /*****
         * Check and set footer sizes
         *****/
        const footerRef = ref(null);
        const { subject: updateFooterSize$ } = rxInitSubjectCallback();
        useObservable(updateFooterSize$.pipe(debounceTime(500), tap(() => {
            if (!footerRef.value)
                return;
            sizeStore.setFooterHeight(footerRef.value.$el.clientHeight);
        })));
        onMounted(() => {
            updateFooterSize$.next('');
        });
        onUpdated(() => {
            updateFooterSize$.next('');
        });
        const { openWindow } = openWindowPlugin();
        /**
         * open external url
         * @param url External url
         */
        function openurl(url) {
            openWindow({ url });
        }
        /**
         * Scroll to top of page
         */
        function scrollTop() {
            window?.scrollTo(0, 0);
        }
        return {
            t,
            todayYear,
            styleType,
            centralShowFooter,
            getSpacesList,
            getPropertyList,
            tacList,
            isHost,
            footerRef,
            updateFooterSize$,
            openurl,
            scrollTop
        };
    }
});
